import {
  type Budget,
  type ContactRequest,
  contactRequestSchema,
  type EngagementType,
  isApiError,
  type Timing,
} from '@portfolio/api-client';
import { useCallback, useState } from 'react';
import { apiClient } from '../../lib/api.js';
import { turnstileEnabled } from '../../lib/turnstile.js';
import { resetTurnstile } from './TurnstileWidget.js';

/**
 * Form state for the hire request.
 *
 * Validation runs against `contactRequestSchema` from the api-client — the
 * same schema the api enforces — so the two cannot disagree about what a
 * valid request is.
 */

export interface ContactFormValues {
  engagement: EngagementType;
  name: string;
  company: string;
  email: string;
  problem: string;
  when: Timing;
  budget: Budget;
}

export const INITIAL_VALUES: ContactFormValues = {
  engagement: 'architecture_review',
  name: '',
  company: '',
  email: '',
  problem: '',
  when: 'within_a_month',
  budget: 'prefer_to_discuss',
};

export type SubmitState =
  | { status: 'idle' }
  | { status: 'sending' }
  | { status: 'sent'; reference: string }
  | { status: 'failed'; reason: 'rate_limited' | 'error' | 'verification' };

type FieldErrors = Partial<Record<keyof ContactFormValues, string>>;

function toRequest(values: ContactFormValues, turnstileToken?: string): ContactRequest {
  return {
    engagement: values.engagement,
    name: values.name.trim(),
    // An empty optional field must be absent, not an empty string.
    ...(values.company.trim() ? { company: values.company.trim() } : {}),
    email: values.email.trim(),
    problem: values.problem.trim(),
    when: values.when,
    // "Prefer to discuss" is the absence of an answer, not an answer.
    ...(values.budget === 'prefer_to_discuss' ? {} : { budget: values.budget }),
    ...(turnstileToken ? { turnstileToken } : {}),
  };
}

/** Keeps the first issue per field; a later one would overwrite the clearest. */
function issuesToFieldErrors(issues: readonly { path: PropertyKey[]; message: string }[]) {
  const errors: FieldErrors = {};
  for (const issue of issues) {
    const field = issue.path[0];
    if (typeof field === 'string' && !(field in errors)) {
      errors[field as keyof ContactFormValues] = issue.message;
    }
  }
  return errors;
}

function apiFieldsToFieldErrors(fields: readonly { field: string; message: string }[]) {
  const errors: FieldErrors = {};
  for (const entry of fields) {
    errors[entry.field as keyof ContactFormValues] = entry.message;
  }
  return errors;
}

/** Maps a rejected submission onto the outcome the toast reports. */
function failureReason(error: unknown): 'rate_limited' | 'error' | 'verification' {
  if (!isApiError(error)) return 'error';
  if (error.kind === 'rate_limited') return 'rate_limited';
  // 403 is what the api returns when Turnstile refuses the token.
  if (error.status === 403) return 'verification';
  return 'error';
}

export function useContactForm() {
  const [values, setValues] = useState<ContactFormValues>(INITIAL_VALUES);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitState, setSubmitState] = useState<SubmitState>({ status: 'idle' });
  // Null until the challenge is solved, and again once it expires.
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileBroken, setTurnstileBroken] = useState(false);

  const setField = useCallback(
    <Key extends keyof ContactFormValues>(field: Key, value: ContactFormValues[Key]) => {
      setValues((current) => ({ ...current, [field]: value }));
      // Clear the field's error as it is edited; re-validating on every
      // keystroke would flag an email as invalid while it is being typed.
      setErrors((current) => {
        if (!(field in current)) return current;
        const { [field]: _removed, ...rest } = current;
        return rest;
      });
    },
    [],
  );

  const submit = useCallback(async (): Promise<void> => {
    const parsed = contactRequestSchema.safeParse(toRequest(values, turnstileToken ?? undefined));

    if (!parsed.success) {
      setErrors(issuesToFieldErrors(parsed.error.issues));
      return;
    }

    // The api rejects a request with no token once verification is on, so
    // stop here rather than spending an attempt against the rate limit.
    if (turnstileEnabled && !turnstileToken) {
      setSubmitState({ status: 'failed', reason: 'verification' });
      return;
    }

    setErrors({});
    setSubmitState({ status: 'sending' });

    try {
      const response = await apiClient.contact.submit(parsed.data);
      setSubmitState({ status: 'sent', reference: response.reference });
      setValues(INITIAL_VALUES);
      // Tokens are single-use; the next submission needs a fresh challenge.
      setTurnstileToken(null);
      resetTurnstile();
    } catch (error) {
      // The api validates against the same schema, so a 422 means the
      // browser's copy disagreed — surface it on the fields.
      if (isApiError(error) && error.kind === 'validation') {
        setErrors(apiFieldsToFieldErrors(error.fields));
        setSubmitState({ status: 'idle' });
        return;
      }

      // A rejected or reused token has to be discarded either way.
      setTurnstileToken(null);
      resetTurnstile();

      setSubmitState({ status: 'failed', reason: failureReason(error) });
    }
  }, [values, turnstileToken]);

  const dismissStatus = useCallback(() => setSubmitState({ status: 'idle' }), []);

  return {
    values,
    errors,
    submitState,
    setField,
    submit,
    dismissStatus,
    setTurnstileToken,
    turnstileBroken,
    reportTurnstileBroken: useCallback(() => setTurnstileBroken(true), []),
    // With the challenge unsolved or broken there is nothing to send.
    canSubmit: !turnstileEnabled || (Boolean(turnstileToken) && !turnstileBroken),
  };
}
